/* eslint-disable max-lines */
import { api } from "jiffy-api";
import { createPromoViewObserver, track } from "jiffy-analytics";
import { EventBus, Events } from "jiffy-store";

const BANNER_SELECTOR = ".js-pdp-banner";
const MAX_CACHE_SIZE = 50;
const ACTIVE_SLIDE_SELECTOR = ".js-color-variant-selector-active";

const SELECTORS = {
  CTA_BUTTON: ".js-pdp-banner-cta-button",
  CTA_LINK: ".js-pdp-banner-cta-link",
  DELIVERY_DATE: ".js-pdp-banner-delivery-date"
};

const getActiveSlideCompositionId = () =>
  document.querySelector(ACTIVE_SLIDE_SELECTOR)?.dataset?.compositionId;

const getPdpCatalogNumber = () =>
  document.querySelector(".js-design-upload-cta")?.dataset?.catalogNumber;

const getCurrentColor = () =>
  new URLSearchParams(window.location.search).get("ac") ||
  document.querySelector(ACTIVE_SLIDE_SELECTOR)?.dataset?.color;

export const deliveryDateCache = new Map();
const inFlightRequests = new Map();
const bannerInstances = new WeakMap();

/**
 * @param {string} productSlug
 * @param {string} compositionName
 * @param {number|undefined} compositionId
 * @returns {string}
 */
const cacheKey = (productSlug, compositionName, compositionId) =>
  `${productSlug}::${compositionName || ""}::${compositionId ?? ""}`;

const isFastEtaEnabled = () => window?.jiffy?.flip?.fastDeliveryEstimate === true;

const callPrintDeliveryApi = async compositionId => {
  if (isFastEtaEnabled()) {
    try {
      const response = await api.product.fetchETA(
        { business_line: "print" },
        { composition_hash: JSON.stringify({ [compositionId]: 1 }), source: "pdp" }
      );
      if (response?.success && response?.estimates) {
        const estimate = response.estimates[compositionId];
        if (estimate?.date) {
          return { composition_dates: { value_full: estimate.date } };
        }
      }
    } catch {
      // Fall through to legacy API
    }
  }
  return api.print.estimatedDelivery({ composition_id: compositionId });
};

const callDeliveryApi = (productSlug, compositionName, compositionId) =>
  compositionId
    ? callPrintDeliveryApi(compositionId)
    : api.product.fetchDeliveryEstimatesForCurrentCart(
        { productId: productSlug },
        { composition_name: compositionName, variants_hash: JSON.stringify({}) }
      );

const performDeliveryRequest = async (productSlug, compositionName, compositionId) => {
  const key = cacheKey(productSlug, compositionName, compositionId);
  try {
    const response = await callDeliveryApi(productSlug, compositionName, compositionId);
    const date = response?.composition_dates?.value_full;
    if (!date) return null;

    if (deliveryDateCache.size >= MAX_CACHE_SIZE) {
      const oldestKey = deliveryDateCache.keys().next().value;
      deliveryDateCache.delete(oldestKey);
    }
    deliveryDateCache.set(key, date);
    return date;
  } catch (error) {
    console.warn("Failed to fetch delivery date:", error);
    return null;
  } finally {
    inFlightRequests.delete(key);
  }
};

const fetchDeliveryDate = (productSlug, compositionName, compositionId) => {
  const key = cacheKey(productSlug, compositionName, compositionId);
  if (deliveryDateCache.has(key)) return Promise.resolve(deliveryDateCache.get(key));
  if (inFlightRequests.has(key)) return inFlightRequests.get(key);

  const request = performDeliveryRequest(productSlug, compositionName, compositionId);
  inFlightRequests.set(key, request);
  return request;
};

export class PromotionBannerClass {
  /**
   * @param {Object} props
   * @param {HTMLElement} props.container
   * @param {Function|null} [props.onButtonClick]
   * @param {Function|null} [props.onLinkClick]
   */
  constructor({ container = null, onButtonClick = null, onLinkClick = null } = {}) {
    this.$container = container;
    this.onButtonClick = onButtonClick;
    this.onLinkClick = onLinkClick;
  }

  get $button() {
    return this.$container?.querySelector(SELECTORS.CTA_BUTTON);
  }

  get $link() {
    return this.$container?.querySelector(SELECTORS.CTA_LINK);
  }

  get $deliveryDates() {
    return [...(this.$container?.querySelectorAll(SELECTORS.DELIVERY_DATE) || [])];
  }

  /** @returns {Promise<void>} */
  async initDeliveryDate() {
    const $elements = this.$deliveryDates;
    if (!$elements.length) return;

    const { deliveryProductSlug, deliveryCompositionName, deliveryCompositionId } =
      this.$container.dataset;
    if (!deliveryProductSlug) return;

    const compositionId = deliveryCompositionId
      ? Number(
          this.currentCompositionId ??
            getActiveSlideCompositionId() ??
            window.jiffy_made_product_props?.composition_id ??
            deliveryCompositionId
        )
      : undefined;

    const date = await fetchDeliveryDate(
      deliveryProductSlug,
      deliveryCompositionName,
      compositionId
    );
    if (date) {
      $elements.forEach($element => {
        $element.textContent = date;
      });
    }
  }

  /** @returns {void} */
  initDeliveryDateOnVisible() {
    if (!this.$deliveryDates.length || !this.$container.dataset.deliveryProductSlug) return;

    const MAX_OCCLUSION_ATTEMPTS = 3;
    let occlusionAttempts = 0;

    this.deliveryObserver = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;

        const rect = this.$container.getBoundingClientRect();
        const topElement = document.elementFromPoint(
          rect.left + rect.width / 2,
          rect.top + rect.height / 2
        );
        const isOccluded = !topElement || !this.$container.contains(topElement);

        if (isOccluded) {
          occlusionAttempts += 1;
          if (occlusionAttempts < MAX_OCCLUSION_ATTEMPTS) return;
        }

        this.deliveryObserver.disconnect();
        this.deliveryObserver = null;
        this.initDeliveryDate();
      },
      { threshold: 0.5 }
    );

    this.deliveryObserver.observe(this.$container);
  }

  /** @returns {void} */
  initViewTracking() {
    const { bannerLocation = "", promoShown = "", promoVariant = "" } = this.$container.dataset;
    this.viewObserver = createPromoViewObserver(this.$container, bannerLocation, {
      promoShown,
      promoVariant
    });
  }

  /**
   * @param {Element} $el
   * @returns {void}
   */
  static trackClick($el) {
    track("promo_click", {
      cta_text: $el.textContent.trim(),
      destination: $el.href || ""
    });
  }

  /** @returns {boolean} */
  get isPrintBanner() {
    return !!this.$container?.dataset?.deliveryCompositionId;
  }

  /** @returns {void} */
  updateCtaForCurrentProduct() {
    const $btn = this.$button;
    if (!$btn || !this.isPrintBanner) return;

    const href = $btn.getAttribute("href");
    if (!href || !href.startsWith("/design-lab")) return;

    const catalogNumber = getPdpCatalogNumber();
    const color = this.currentColor || getCurrentColor();
    if (!catalogNumber && !color) return;

    const url = new URL(href, window.location.origin);
    if (catalogNumber) url.searchParams.set("catalog_number", catalogNumber);
    if (color) url.searchParams.set("ac", color);
    $btn.href = url.pathname + url.search;
  }

  /** @returns {boolean} */
  init() {
    if (!this.$container) return false;

    this.handleButtonClick = event => {
      PromotionBannerClass.trackClick(event.currentTarget);
      this.onButtonClick?.(event);
    };

    this.handleLinkClick = event => {
      PromotionBannerClass.trackClick(event.currentTarget);
      this.onLinkClick?.(event);
    };

    if (this.$button) {
      this.$button.addEventListener("click", this.handleButtonClick);
    }

    if (this.$link) {
      this.$link.addEventListener("click", this.handleLinkClick);
    }

    this.initViewTracking();
    this.initDeliveryDateOnVisible();
    this.updateCtaForCurrentProduct();

    if (this.isPrintBanner) {
      this.handleColorChange = (_, { compositionId, color }) => {
        this.currentCompositionId = compositionId;
        this.currentColor = color;
        this.initDeliveryDate();
        this.updateCtaForCurrentProduct();
      };
      EventBus.on(Events.PRODUCT_COLOR_SELECTED, this.handleColorChange);
    }

    return true;
  }

  /** @returns {void} */
  destroy() {
    if (this.$button && this.handleButtonClick) {
      this.$button.removeEventListener("click", this.handleButtonClick);
      this.handleButtonClick = null;
    }

    if (this.$link && this.handleLinkClick) {
      this.$link.removeEventListener("click", this.handleLinkClick);
      this.handleLinkClick = null;
    }

    if (this.viewObserver) {
      this.viewObserver.disconnect();
      this.viewObserver = null;
    }

    if (this.deliveryObserver) {
      this.deliveryObserver.disconnect();
      this.deliveryObserver = null;
    }

    if (this.handleColorChange) {
      EventBus.off(Events.PRODUCT_COLOR_SELECTED, this.handleColorChange);
      this.handleColorChange = null;
    }
  }
}

export const initAllBanners = () => {
  const $containers = document.querySelectorAll(BANNER_SELECTOR);
  $containers.forEach($container => {
    const existingBanner = bannerInstances.get($container);
    if (existingBanner) {
      existingBanner.initDeliveryDate();
      return;
    }

    const banner = new PromotionBannerClass({ container: $container });
    if (banner.init()) {
      bannerInstances.set($container, banner);
    }
  });
};

/** Auto-initializes any `.js-pdp-banner` added to the DOM after initial load. */
export const observeBannerInsertions = () => {
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      Array.from(mutation.addedNodes)
        .filter(node => node.nodeType === Node.ELEMENT_NODE)
        .forEach(node => {
          const targets = node.matches?.(BANNER_SELECTOR)
            ? [node]
            : [...(node.querySelectorAll?.(BANNER_SELECTOR) || [])];

          targets
            .filter($container => !bannerInstances.has($container))
            .forEach($container => {
              const banner = new PromotionBannerClass({ container: $container });
              if (banner.init()) bannerInstances.set($container, banner);
            });
        });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
  return observer;
};
